import { createSelector } from "@reduxjs/toolkit";
import { BackendValidationErrorViewModel, FormValidationError, FormValidationErrorSeverity } from "../global-types/store-errors";
import { contouringSelectors } from "./contouringSlice";
import { ContouringRoi, ContourCustomizationSelection, isContouringCustomizationOutput } from "./contouring-types";
import { CustomizationBaseWithModelName, CustomizationObjectType, CustomizationOutput, ModelVisibility, ValidationStatus, worstValidationStatus } from "../global-types/customization-types";
import { naturalSort } from "../../util/sort";
import { userSelectors } from "../user/userSlice";
import { StoreState } from "../store";
import { generateCustomizationTreeViewItems, SelectorWithInputArg } from "../global-types/customizationSelectors";
import { generateCustomizationLabel } from "../global-types/customization-helpers";
import { CommonContourSelectors } from "../global-types/modelTypeSelectorHelper/commonContourSelectors";

////////////////////
//
// A collection of a bit more complex selectors (mainly factory functions) for contouringConfig.
// The more straightforward selectors are embedded directly into contouringConfigSlice.
//
////////////////////


const commonSelectors = new CommonContourSelectors();

export const contourCommonSelectors = {
    selectDeprecatedModels: (state: StoreState) => commonSelectors.selectDeprecatedModels(state.contouring),
    selectNonLicensedModels: (state: StoreState) => commonSelectors.selectNonLicensedModels(state.contouring),
    selectUserHiddenModels: (state: StoreState) => commonSelectors.selectUserHiddenModels(state.contouring),
    selectLatestModels: (state: StoreState) => commonSelectors.selectLatestModels(state.contouring),
    selectModelsInAlphabeticalOrder: (state: StoreState) => commonSelectors.selectModelsInAlphabeticalOrder(state.contouring),
    selectAllVersionTagsInOrder: (state: StoreState) => commonSelectors.selectAllVersionTagsInOrder(state.contouring),
    selectAllTagsInOrder: (state: StoreState) => commonSelectors.selectAllTagsInOrder(state.contouring),
    selectInputModalities: (state: StoreState) => commonSelectors.selectInputModalities(state.contouring),
    selectOutputModalities: (state: StoreState) => commonSelectors.selectOutputModalities(state.contouring),
}


/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific roi customization. */
export const makeSelectFormValidationErrorsForRoi = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, roiId: string) => roiId, contouringSelectors.selectFormValidationErrors],
    (roiId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === roiId && e.itemType === CustomizationObjectType.Roi)
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific dicom rule. */
export const makeSelectFormValidationErrorsForDicomRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, dicomRuleId: string) => dicomRuleId, contouringSelectors.selectFormValidationErrors],
    (dicomRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === dicomRuleId && e.itemType === CustomizationObjectType.DicomRule)
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific dicom attribute rule. */
export const makeSelectFormValidationErrorsForDicomAttributeRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, dicomAttributeRuleId: string) => dicomAttributeRuleId, contouringSelectors.selectFormValidationErrors],
    (dicomAttributeRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === dicomAttributeRuleId && e.itemType === CustomizationObjectType.DicomAttributeRule)
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific AE Title rule. */
export const makeSelectFormValidationErrorsForAeTitleRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, aeTitleRuleId: string) => aeTitleRuleId, contouringSelectors.selectFormValidationErrors],
    (aeTitleRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === aeTitleRuleId && e.itemType === CustomizationObjectType.AeTitleRule)
);

/** Factory function for making a memoized selector that returns all roi customization objects that belong to a specific customization output. */
export const makeSelectRoiCustomizationsForOutput = (): SelectorWithInputArg<ContouringRoi[], string> => createSelector(
    [contouringSelectors.selectRois, (_, customizationOutputId: string) => customizationOutputId],
    (roiCustomizations, customizationOutputId) => roiCustomizations.filter(r => r.customizationOutputId === customizationOutputId)
);

/**
 * Factory function for making a memoized selector that returns true if any metadata for given output id is modified.
 */
export const makeSelectIsAnyMetadataForOutputModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [
        (state: StoreState, customizationOutputId: string) => contouringSelectors.selectOutputById(state, customizationOutputId),
        (state: StoreState) => contouringSelectors.selectOutputMetadataEntities(state)
    ],
    (customizationOutput, metadataEntities) => customizationOutput?.metadata.some(mId => metadataEntities[mId]?.isModified) || false
);

/**
 * Factory function for making a memoized selector that returns true if any ROI for given output id is modified.
 */
export const makeSelectIsAnyRoiForOutputModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [
        (state: StoreState, customizationOutputId) => contouringSelectors.selectOutputById(state, customizationOutputId),
        (state: StoreState) => contouringSelectors.selectRoiEntities(state)
    ],
    (customizationOutput, roiCustomizationEntities) => customizationOutput?.rois.some(rId => roiCustomizationEntities[rId]?.isModified) || false
);

/**
 * Factory function for making a memoized selector that returns true if any selection rule (trigger) for a customization base id is modified.
 */
export const makeSelectIsAnyTriggerForBaseModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [
        (state: StoreState, customizationBaseId) => contouringSelectors.selectCustomizationBaseById(state, customizationBaseId),
        (state: StoreState) => contouringSelectors.selectAeTitleRuleEntities(state),
        (state: StoreState) => contouringSelectors.selectDicomRuleEntities(state)
    ],
    (customizationBase, aeTitleRuleEntities, dicomRuleEntities) => {
        const isAnyAeTitleRuleModified = customizationBase?.aeTitleRules.some(aId => aeTitleRuleEntities[aId]?.isModified) || false;
        if (isAnyAeTitleRuleModified) { return true; }
        return customizationBase?.dicomRules.some(dId => dicomRuleEntities[dId]?.isModified === true) || false;
    }
);

/**
 * Returns customization base IDs and their names in alphabetical order. Takes current filtering
 * and model visibility settings into account.
 */
export const selectCustomizationBasesInNameOrder = createSelector(
    [(state: StoreState) => contouringSelectors.selectCustomizationBases(state),
    (state: StoreState) => contourCommonSelectors.selectModelsInAlphabeticalOrder(state),
    (state: StoreState) => state.contouring.areDeprecatedModelsShown,
    (state: StoreState) => state.contouring.areNonLicensedModelsShown,
    (state: StoreState) => state.contouring.areUserHiddenModelsShown],
    (customizations, models, areDeprecatedModelsShown, areNonLicensedModelsShown, areUserHiddenModelsShown) => {

        const customizationNamesWithIds: CustomizationBaseWithModelName[] = [];

        // only use customizations from models that we have pre-filtered to be visible
        for (const model of models) {
            // filter customizations per current user config
            const filteredCustomizations = customizations.filter(c => {
                if (c.modelId !== model.id) {
                    // this customization does not belong to the model we're iterating right now
                    return false;
                }

                if (model.visibility === ModelVisibility.AlwaysShown) { return true; }
                if (model.visibility === ModelVisibility.AlwaysHidden) { return areUserHiddenModelsShown; }
                if (!model.isAvailable && !areNonLicensedModelsShown) { return false; }
                if (model.isDeprecated && !areDeprecatedModelsShown) { return false; }
                return true;
            });

            const customizationNamesWithIdsForModel: CustomizationBaseWithModelName[] = filteredCustomizations.map(c => ({
                customizationBaseId: c.id,
                label: `${model.label || ''} ${c.customizationName}`
            }));

            customizationNamesWithIds.push(...customizationNamesWithIdsForModel);
        }

        return naturalSort(customizationNamesWithIds, 'label');
    });

/**
 * Selector to map model version tags to matching customization bases. 
 */
export const selectVersionTagsForCustomizationBases = createSelector([
    (state: StoreState) => contouringSelectors.selectModels(state),
    (state: StoreState) => contourCommonSelectors.selectAllVersionTagsInOrder(state),
], (models, allVersionTags) => {
    // create a map of version tags (string[]) for customization bases (ids)
    const mapOfVersionTags: Record<string, string[]> = {};

    for (const model of models) {
        const versionTags: string[] = [];
        if (model.isLatest) {
            versionTags.push('latest');
        }
        if (model.version) {
            versionTags.push(model.version);
        }
        
        model.customizations.forEach(customizationBaseId => mapOfVersionTags[customizationBaseId] = versionTags);

    }

    return mapOfVersionTags;
});

/** Generate a tree view hierarchy of models and some of their child objects. This is used by the customization page side navigation.
 * This data isn't ready-to-go for Patternfly's TreeView component but the separate convertTreeViewDataIntoItemHierarchy helper function
 * in customization-helpers.tsx is used to generate the appropriate data from what this function produces.
 */
export const selectContourTreeViewItems = createSelector([
    (state: StoreState) => contourCommonSelectors.selectModelsInAlphabeticalOrder(state),
    (state: StoreState) => contouringSelectors.selectCustomizationBasesInAlphabeticalOrder(state),
    (state: StoreState) => contouringSelectors.selectOutputEntities(state),
    (state: StoreState) => contouringSelectors.selectOutputMetadataEntities(state),
    (state: StoreState) => contouringSelectors.selectCustomizationValidationErrorEntities(state),
    (state: StoreState) => contouringSelectors.selectFormValidationErrors(state),
    (state: StoreState) => contouringSelectors.selectDicomRuleEntities(state),
],
    (models, customizationBases, outputEntities, metadataEntities, validationEntities, formValidationErrors, dicomRuleEntities) => {

        // create an inline validation function that will be supplied to tree view generator function
        const areOutputChildrenValid = (output: CustomizationOutput, validationEntities: Record<string, BackendValidationErrorViewModel>, formErrors: FormValidationError[]): ValidationStatus => {
            if (!isContouringCustomizationOutput(output)) {
                throw new Error('Invalid output type');
            }
            const hasBackendRoiError = !output.rois.every(rId => !validationEntities[rId] || validationEntities[rId]!.type !== CustomizationObjectType.Roi);
            let status: ValidationStatus = hasBackendRoiError ? ValidationStatus.Error : ValidationStatus.Valid;

            // Check form errors on ROIs belonging to this output
            for (const err of formErrors) {
                if (output.rois.includes(err.itemId)) {
                    if (err.severity === FormValidationErrorSeverity.Error) {
                        return ValidationStatus.Error;
                    }
                    if (err.severity === FormValidationErrorSeverity.Warning) {
                        status = worstValidationStatus(status, ValidationStatus.Warning);
                    }
                }
            }

            return status;
        }

        const results = generateCustomizationTreeViewItems(models, customizationBases, outputEntities, metadataEntities, validationEntities, formValidationErrors, areOutputChildrenValid, dicomRuleEntities);
        return results;
    });



/**
 * Returns all Contour+ customizations without any filtering. Used for finding initial customizations
 * that may not pass current filter settings. Used by the Image+ customization page for selecting a 
 * contouring model (and customization).
 */
export const selectAllContourModelsAndCustomizations = createSelector([
    (state: StoreState) => contouringSelectors.selectCustomizationBases(state),
    (state: StoreState) => contouringSelectors.selectModels(state),
], (allCustomizationBases, allModels) => {
    const customizationsForUi: ContourCustomizationSelection[] = [];

    for (const model of allModels) {
        for (const customizationBase of allCustomizationBases.filter(b => b.modelId === model.id)) {
            customizationsForUi.push({
                customizationBaseId: customizationBase.id,
                modelId: model.id,
                customizationName: customizationBase.customizationName,
                modelName: model.modelName,
                label: model.label,
                bodyPart: model.bodyPart,
                description: model.description,
                visibility: model.visibility,
                tags: model.tags,
                inputModalities: model.inputModalities,
                outputModalities: model.outputModalities,
                isAvailable: model.isAvailable,
                isDeprecated: model.isDeprecated,
                isLatest: model.isLatest,
                version: model.version,
                action: `${model.modelName}.${customizationBase.customizationName}`,
                customizationLabel: generateCustomizationLabel(model.label, customizationBase.customizationName),
            });
        }
    }

    return naturalSort(customizationsForUi, 'customizationLabel');
});

/**
 * Returns a list of Contour+ customizations that can be used in the UI. Used by the Image+ customization
 * page for selecting a contouring model (and customization). Applies visibility, tag, modality, and version filters.
 */
export const selectContourModelsAndCustomizations = createSelector([
    (state: StoreState) => selectCustomizationBasesInNameOrder(state),
    (state: StoreState) => selectAllContourModelsAndCustomizations(state),
], (filteredCustomizationBases, allCustomizations) => {
    // Create a Set of filtered customization base IDs for quick lookup
    const filteredCustomizationBaseIds = new Set(filteredCustomizationBases.map(c => c.customizationBaseId));

    // Only include customizations that passed filtering (visibility, tags, modalities, version)
    return allCustomizations.filter(customization =>
        filteredCustomizationBaseIds.has(customization.customizationBaseId)
    );
});
